import React from 'react';
import {
  ResponsiveContainer,
  LineChart as RLineChart,
  BarChart as RBarChart,
  AreaChart as RAreaChart,
  PieChart as RPieChart,
  RadarChart as RRadarChart,
  Line,
  Bar,
  Area,
  Pie,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ─── Dark-mode-aware Tooltip ────────────────────────────────── */
function ChartTooltipContent({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name?: string; value?: number | string; color?: string; dataKey?: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-gray-600 dark:bg-gray-800">
      {label != null && (
        <p className="mb-1 font-medium text-gray-700 dark:text-gray-200">{label}</p>
      )}
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <span
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-600 dark:text-gray-300">
            {entry.name}: <strong className="text-gray-900 dark:text-gray-100">{entry.value}</strong>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Theme Colors ──────────────────────────────────────────── */
const CHART_COLORS = [
  '#4c6ef5', // primary-600
  '#f06595', // secondary-500
  '#20c997', // accent-500
  '#fab005', // amber
  '#7048e8', // violet
  '#15aabf', // cyan
  '#ff6b6b', // red
  '#51cf66', // green
];

/* ─── Shared Wrapper ────────────────────────────────────────── */
interface ChartWrapperProps {
  loading?: boolean;
  empty?: boolean;
  emptyMessage?: string;
  height?: number;
  className?: string;
  children: React.ReactNode;
}

function ChartWrapper({
  loading,
  empty,
  emptyMessage = 'No data available',
  height = 300,
  className,
  children,
}: ChartWrapperProps) {
  if (loading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
          className
        )}
        style={{ height }}
      >
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (empty) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-xl border border-gray-200 bg-white text-sm text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-500',
          className
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children as React.ReactElement}
      </ResponsiveContainer>
    </div>
  );
}

/* ─── Shared Types ──────────────────────────────────────────── */
export interface SeriesConfig {
  dataKey: string;
  name?: string;
  color?: string;
}

interface BaseChartProps {
  data: Record<string, unknown>[];
  loading?: boolean;
  emptyMessage?: string;
  height?: number;
  className?: string;
}

/* ─── LineChart ──────────────────────────────────────────────── */
export interface LineChartProps extends BaseChartProps {
  xKey: string;
  series: SeriesConfig[];
  showGrid?: boolean;
  showLegend?: boolean;
}

export function LineChart({
  data,
  xKey,
  series,
  loading,
  emptyMessage,
  height,
  className,
  showGrid = true,
  showLegend = true,
}: LineChartProps) {
  return (
    <ChartWrapper loading={loading} empty={!data.length} emptyMessage={emptyMessage} height={height} className={className}>
      <RLineChart data={data}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />}
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} stroke="currentColor" className="text-gray-400" />
        <YAxis tick={{ fontSize: 12 }} stroke="currentColor" className="text-gray-400" />
        <Tooltip content={<ChartTooltipContent />} />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s, i) => (
          <Line
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            name={s.name ?? s.dataKey}
            stroke={s.color ?? CHART_COLORS[i % CHART_COLORS.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </RLineChart>
    </ChartWrapper>
  );
}

/* ─── BarChart ───────────────────────────────────────────────── */
export interface BarChartProps extends BaseChartProps {
  xKey: string;
  series: SeriesConfig[];
  showGrid?: boolean;
  showLegend?: boolean;
  stacked?: boolean;
}

export function BarChart({
  data,
  xKey,
  series,
  loading,
  emptyMessage,
  height,
  className,
  showGrid = true,
  showLegend = true,
  stacked = false,
}: BarChartProps) {
  return (
    <ChartWrapper loading={loading} empty={!data.length} emptyMessage={emptyMessage} height={height} className={className}>
      <RBarChart data={data}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />}
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} stroke="currentColor" className="text-gray-400" />
        <YAxis tick={{ fontSize: 12 }} stroke="currentColor" className="text-gray-400" />
        <Tooltip content={<ChartTooltipContent />} />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s, i) => (
          <Bar
            key={s.dataKey}
            dataKey={s.dataKey}
            name={s.name ?? s.dataKey}
            fill={s.color ?? CHART_COLORS[i % CHART_COLORS.length]}
            radius={[4, 4, 0, 0]}
            stackId={stacked ? 'stack' : undefined}
          />
        ))}
      </RBarChart>
    </ChartWrapper>
  );
}

/* ─── AreaChart ──────────────────────────────────────────────── */
export interface AreaChartProps extends BaseChartProps {
  xKey: string;
  series: SeriesConfig[];
  showGrid?: boolean;
  showLegend?: boolean;
  stacked?: boolean;
}

export function AreaChart({
  data,
  xKey,
  series,
  loading,
  emptyMessage,
  height,
  className,
  showGrid = true,
  showLegend = true,
  stacked = false,
}: AreaChartProps) {
  return (
    <ChartWrapper loading={loading} empty={!data.length} emptyMessage={emptyMessage} height={height} className={className}>
      <RAreaChart data={data}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-200 dark:text-gray-700" />}
        <XAxis dataKey={xKey} tick={{ fontSize: 12 }} stroke="currentColor" className="text-gray-400" />
        <YAxis tick={{ fontSize: 12 }} stroke="currentColor" className="text-gray-400" />
        <Tooltip content={<ChartTooltipContent />} />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s, i) => {
          const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
          return (
            <Area
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              name={s.name ?? s.dataKey}
              stroke={color}
              fill={color}
              fillOpacity={0.15}
              strokeWidth={2}
              stackId={stacked ? 'stack' : undefined}
            />
          );
        })}
      </RAreaChart>
    </ChartWrapper>
  );
}

/* ─── PieChart ───────────────────────────────────────────────── */
export interface PieChartProps extends BaseChartProps {
  dataKey: string;
  nameKey: string;
  colors?: string[];
  showLegend?: boolean;
  innerRadius?: number;
}

export function PieChart({
  data,
  dataKey,
  nameKey,
  loading,
  emptyMessage,
  height,
  className,
  colors,
  showLegend = true,
  innerRadius = 0,
}: PieChartProps) {
  const palette = colors ?? CHART_COLORS;

  return (
    <ChartWrapper loading={loading} empty={!data.length} emptyMessage={emptyMessage} height={height} className={className}>
      <RPieChart>
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius="75%"
          label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
          fontSize={11}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={palette[i % palette.length]} />
          ))}
        </Pie>
        <Tooltip content={<ChartTooltipContent />} />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
      </RPieChart>
    </ChartWrapper>
  );
}

/* ─── RadarChart ─────────────────────────────────────────────── */
export interface RadarChartProps extends BaseChartProps {
  angleKey: string;
  series: SeriesConfig[];
  showLegend?: boolean;
}

export function RadarChart({
  data,
  angleKey,
  series,
  loading,
  emptyMessage,
  height,
  className,
  showLegend = true,
}: RadarChartProps) {
  return (
    <ChartWrapper loading={loading} empty={!data.length} emptyMessage={emptyMessage} height={height} className={className}>
      <RRadarChart data={data}>
        <PolarGrid stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
        <PolarAngleAxis dataKey={angleKey} tick={{ fontSize: 11 }} stroke="currentColor" className="text-gray-400" />
        <PolarRadiusAxis tick={{ fontSize: 10 }} stroke="currentColor" className="text-gray-300" />
        <Tooltip content={<ChartTooltipContent />} />
        {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
        {series.map((s, i) => {
          const color = s.color ?? CHART_COLORS[i % CHART_COLORS.length];
          return (
            <Radar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name ?? s.dataKey}
              stroke={color}
              fill={color}
              fillOpacity={0.2}
            />
          );
        })}
      </RRadarChart>
    </ChartWrapper>
  );
}

/* ─── Chart.js–compatible wrapper (dashboards v2) ───────────────────────────
 * Les écrans RH importent <Chart type="bar" …> avec des données type Chart.js.
 * Ce composant mappe vers BarChart / LineChart / PieChart / RadarChart (Recharts).
 */
export type ChartJsLikeData = {
  labels?: string[];
  datasets?: Array<{
    label?: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string;
  }>;
};

export function Chart({
  type,
  data,
  options: _options,
}: {
  type: 'bar' | 'line' | 'pie' | 'doughnut' | 'radar';
  data: ChartJsLikeData;
  options?: Record<string, unknown>;
}) {
  const labels = data.labels ?? [];
  const datasets = data.datasets ?? [];
  if (!labels.length || !datasets.length) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 text-sm text-gray-400 dark:border-gray-700 dark:bg-gray-900/40">
        Aucune donnée
      </div>
    );
  }

  if (type === 'bar' || type === 'line') {
    const rows: Record<string, unknown>[] = labels.map((name, i) => {
      const row: Record<string, unknown> = { name };
      datasets.forEach((ds, j) => {
        row[`s${j}`] = ds.data[i] ?? 0;
      });
      return row;
    });
    const series = datasets.map((ds, j) => ({
      dataKey: `s${j}`,
      name: ds.label ?? `Série ${j + 1}`,
    }));
    if (type === 'bar') {
      return <BarChart data={rows} xKey="name" series={series} height={280} />;
    }
    return <LineChart data={rows} xKey="name" series={series} height={280} />;
  }

  if (type === 'pie' || type === 'doughnut') {
    const ds = datasets[0];
    if (!ds) return null;
    const pie = labels.map((name, i) => ({
      name,
      value: ds.data[i] ?? 0,
    }));
    const bg = ds.backgroundColor;
    const colors = Array.isArray(bg) ? bg : undefined;
    const innerRadius = type === 'doughnut' ? 55 : 0;
    return (
      <PieChart
        data={pie}
        dataKey="value"
        nameKey="name"
        height={280}
        colors={colors}
        innerRadius={innerRadius}
        showLegend
      />
    );
  }

  if (type === 'radar') {
    const angleKey = 'skill';
    const rows: Record<string, unknown>[] = labels.map((name, i) => {
      const row: Record<string, unknown> = { [angleKey]: name };
      datasets.forEach((ds, j) => {
        row[`s${j}`] = ds.data[i] ?? 0;
      });
      return row;
    });
    const series = datasets.map((ds, j) => ({
      dataKey: `s${j}`,
      name: ds.label ?? `Série ${j + 1}`,
    }));
    return <RadarChart data={rows} angleKey={angleKey} series={series} height={360} />;
  }

  return null;
}
